import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Container, Header, Segment, Button, Select, Card, Statistic, Dimmer, Loader, Message, Form, Tab, Table } from 'semantic-ui-react';
import { API, showError } from '../../helpers';
import { normalizeNumeric } from '../../helpers/render';
import {
  fromPeriodInputValue,
  getDefaultDateRange,
  normalizeDateRangeForPeriod,
  periodInputMeta,
  toBoundaryTimestamp,
  toPeriodInputValue,
} from './dateRange';
import './DashboardStats.css';

const PERIOD_OPTIONS = [
  { key: 'd', text: '日', value: 'd' },
  { key: 'w', text: '周', value: 'w' },
  { key: 'm', text: '月', value: 'm' },
];

// ---- Sort utilities (matching DashboardOverview/StatsTable) ----

const sortData = (rows, column, direction) => {
  const sorted = [...rows].sort((a, b) => {
    const aVal = normalizeNumeric(a[column]);
    const bVal = normalizeNumeric(b[column]);
    if (aVal !== null && bVal !== null) return aVal - bVal;
    const rawA = a[column] ?? '';
    const rawB = b[column] ?? '';
    if (typeof rawA === 'string') return rawA.localeCompare(rawB);
    return rawA - rawB;
  });
  return direction === 'descending' ? sorted.reverse() : sorted;
};

const amountValue = (value) => normalizeNumeric(value) ?? 0;
const percentValue = (value) => amountValue(value);

const TotalsRow = ({ cells, label }) => (
  <Table.Row
    style={{
      fontWeight: 'bold',
      backgroundColor: '#f5f5f5',
      borderTop: '2px solid #ddd',
    }}
  >
    <Table.Cell>{label}</Table.Cell>
    {cells.map((cell, i) => (
      <Table.Cell key={i}>{cell}</Table.Cell>
    ))}
  </Table.Row>
);

// ---- Sortable Tables ----

const TokenModelTable = ({ rows }) => {
  const [data, setData] = useState(rows);
  const [sortColumn, setSortColumn] = useState(null);
  const [sortDirection, setSortDirection] = useState('ascending');

  useEffect(() => {
    setData(rows);
    setSortColumn(null);
    setSortDirection('ascending');
  }, [rows]);

  const handleSort = (column) => {
    const newDirection =
      sortColumn === column && sortDirection === 'ascending'
        ? 'descending'
        : 'ascending';
    setSortColumn(column);
    setSortDirection(newDirection);
    setData(sortData(rows, column, newDirection));
  };

  const sorted = (col) => (sortColumn === col ? sortDirection : null);

  const totals = rows.reduce(
    (acc, r) => ({
      tokens: acc.tokens + (r.tokens || 0),
      prompt_tokens: acc.prompt_tokens + (r.prompt_tokens || 0),
      completion_tokens: acc.completion_tokens + (r.completion_tokens || 0),
      cache_creation_tokens:
        acc.cache_creation_tokens + (r.cache_creation_tokens || 0),
      cache_read_tokens: acc.cache_read_tokens + (r.cache_read_tokens || 0),
      amount_usd: acc.amount_usd + amountValue(r.amount_usd),
      amount_cny: acc.amount_cny + amountValue(r.amount_cny),
      request_count: acc.request_count + (r.request_count || 0),
    }),
    {
      tokens: 0, prompt_tokens: 0, completion_tokens: 0,
      cache_creation_tokens: 0, cache_read_tokens: 0,
      amount_usd: 0, amount_cny: 0, request_count: 0,
    }
  );

  const hasCache =
    totals.cache_creation_tokens > 0 || totals.cache_read_tokens > 0;

  return (
    <Table celled sortable>
      <Table.Header>
        <Table.Row>
          <Table.HeaderCell sorted={sorted('token_id')} onClick={() => handleSort('token_id')}>令牌ID</Table.HeaderCell>
          <Table.HeaderCell sorted={sorted('token_name')} onClick={() => handleSort('token_name')}>令牌名称</Table.HeaderCell>
          <Table.HeaderCell sorted={sorted('project_number')} onClick={() => handleSort('project_number')}>项目编号</Table.HeaderCell>
          <Table.HeaderCell sorted={sorted('model_name')} onClick={() => handleSort('model_name')}>模型名称</Table.HeaderCell>
          <Table.HeaderCell sorted={sorted('tokens')} onClick={() => handleSort('tokens')}>Token消耗</Table.HeaderCell>
          <Table.HeaderCell>提示Token</Table.HeaderCell>
          <Table.HeaderCell>补全Token</Table.HeaderCell>
          {hasCache && <Table.HeaderCell>缓存创建Token</Table.HeaderCell>}
          {hasCache && <Table.HeaderCell>缓存读取Token</Table.HeaderCell>}
          <Table.HeaderCell sorted={sorted('amount_usd')} onClick={() => handleSort('amount_usd')}>美元</Table.HeaderCell>
          <Table.HeaderCell sorted={sorted('amount_cny')} onClick={() => handleSort('amount_cny')}>人民币</Table.HeaderCell>
          <Table.HeaderCell sorted={sorted('request_count')} onClick={() => handleSort('request_count')}>请求数</Table.HeaderCell>
        </Table.Row>
      </Table.Header>
      <Table.Body>
        {data.map((row, idx) => (
          <Table.Row key={`${row.token_id}_${row.model_name}_${idx}`}>
            <Table.Cell>{row.token_id ?? '-'}</Table.Cell>
            <Table.Cell>{row.token_name ?? '-'}</Table.Cell>
            <Table.Cell>{row.project_number ?? '-'}</Table.Cell>
            <Table.Cell>{row.model_name ?? '-'}</Table.Cell>
            <Table.Cell>{(row.tokens || 0).toLocaleString()}</Table.Cell>
            <Table.Cell>{(row.prompt_tokens || 0).toLocaleString()}</Table.Cell>
            <Table.Cell>{(row.completion_tokens || 0).toLocaleString()}</Table.Cell>
            {hasCache && <Table.Cell>{(row.cache_creation_tokens || 0).toLocaleString()}</Table.Cell>}
            {hasCache && <Table.Cell>{(row.cache_read_tokens || 0).toLocaleString()}</Table.Cell>}
            <Table.Cell>${amountValue(row.amount_usd).toFixed(6)}</Table.Cell>
            <Table.Cell>¥{amountValue(row.amount_cny).toFixed(4)}</Table.Cell>
            <Table.Cell>{(row.request_count || 0).toLocaleString()}</Table.Cell>
          </Table.Row>
        ))}
        {rows.length > 0 && (
          <TotalsRow
            label='合计'
            cells={[
              totals.tokens.toLocaleString(),
              totals.prompt_tokens.toLocaleString(),
              totals.completion_tokens.toLocaleString(),
              ...(hasCache
                ? [totals.cache_creation_tokens.toLocaleString(), totals.cache_read_tokens.toLocaleString()]
                : []),
              `$${totals.amount_usd.toFixed(6)}`,
              `¥${totals.amount_cny.toFixed(4)}`,
              totals.request_count.toLocaleString(),
            ]}
          />
        )}
      </Table.Body>
    </Table>
  );
};

const ModelTable = ({ rows }) => {
  const [data, setData] = useState(rows);
  const [sortColumn, setSortColumn] = useState(null);
  const [sortDirection, setSortDirection] = useState('ascending');

  useEffect(() => {
    setData(rows);
    setSortColumn(null);
    setSortDirection('ascending');
  }, [rows]);

  const handleSort = (column) => {
    const newDirection =
      sortColumn === column && sortDirection === 'ascending'
        ? 'descending'
        : 'ascending';
    setSortColumn(column);
    setSortDirection(newDirection);
    setData(sortData(rows, column, newDirection));
  };

  const sorted = (col) => (sortColumn === col ? sortDirection : null);

  const totals = rows.reduce(
    (acc, r) => ({
      tokens: acc.tokens + (r.tokens || 0),
      amount_usd: acc.amount_usd + amountValue(r.amount_usd),
      amount_cny: acc.amount_cny + amountValue(r.amount_cny),
      request_count: acc.request_count + (r.request_count || 0),
    }),
    { tokens: 0, amount_usd: 0, amount_cny: 0, request_count: 0 }
  );

  return (
    <Table celled sortable>
      <Table.Header>
        <Table.Row>
          <Table.HeaderCell sorted={sorted('model_name')} onClick={() => handleSort('model_name')}>模型名称</Table.HeaderCell>
          <Table.HeaderCell sorted={sorted('tokens')} onClick={() => handleSort('tokens')}>Token消耗</Table.HeaderCell>
          <Table.HeaderCell sorted={sorted('amount_usd')} onClick={() => handleSort('amount_usd')}>美元</Table.HeaderCell>
          <Table.HeaderCell sorted={sorted('amount_cny')} onClick={() => handleSort('amount_cny')}>人民币</Table.HeaderCell>
          <Table.HeaderCell sorted={sorted('request_count')} onClick={() => handleSort('request_count')}>请求数</Table.HeaderCell>
        </Table.Row>
      </Table.Header>
      <Table.Body>
        {data.map((row, idx) => (
          <Table.Row key={row.model_name || idx}>
            <Table.Cell>{row.model_name ?? '-'}</Table.Cell>
            <Table.Cell>{(row.tokens || 0).toLocaleString()}</Table.Cell>
            <Table.Cell>${amountValue(row.amount_usd).toFixed(6)}</Table.Cell>
            <Table.Cell>¥{amountValue(row.amount_cny).toFixed(4)}</Table.Cell>
            <Table.Cell>{(row.request_count || 0).toLocaleString()}</Table.Cell>
          </Table.Row>
        ))}
        {rows.length > 0 && (
          <TotalsRow
            label='合计'
            cells={[
              totals.tokens.toLocaleString(),
              `$${totals.amount_usd.toFixed(6)}`,
              `¥${totals.amount_cny.toFixed(4)}`,
              totals.request_count.toLocaleString(),
            ]}
          />
        )}
      </Table.Body>
    </Table>
  );
};

const TokenTable = ({ rows }) => {
  const [data, setData] = useState(rows);
  const [sortColumn, setSortColumn] = useState(null);
  const [sortDirection, setSortDirection] = useState('ascending');

  useEffect(() => {
    setData(rows);
    setSortColumn(null);
    setSortDirection('ascending');
  }, [rows]);

  const handleSort = (column) => {
    const newDirection =
      sortColumn === column && sortDirection === 'ascending'
        ? 'descending'
        : 'ascending';
    setSortColumn(column);
    setSortDirection(newDirection);
    setData(sortData(rows, column, newDirection));
  };

  const sorted = (col) => (sortColumn === col ? sortDirection : null);

  const totals = rows.reduce(
    (acc, r) => ({
      tokens: acc.tokens + (r.tokens || 0),
      amount_usd: acc.amount_usd + amountValue(r.amount_usd),
      amount_cny: acc.amount_cny + amountValue(r.amount_cny),
      request_count: acc.request_count + (r.request_count || 0),
    }),
    { tokens: 0, amount_usd: 0, amount_cny: 0, request_count: 0 }
  );

  return (
    <Table celled sortable>
      <Table.Header>
        <Table.Row>
          <Table.HeaderCell sorted={sorted('token_name')} onClick={() => handleSort('token_name')}>令牌名称</Table.HeaderCell>
          <Table.HeaderCell sorted={sorted('project_number')} onClick={() => handleSort('project_number')}>项目编号</Table.HeaderCell>
          <Table.HeaderCell sorted={sorted('tokens')} onClick={() => handleSort('tokens')}>Token消耗</Table.HeaderCell>
          <Table.HeaderCell sorted={sorted('amount_usd')} onClick={() => handleSort('amount_usd')}>美元</Table.HeaderCell>
          <Table.HeaderCell sorted={sorted('amount_cny')} onClick={() => handleSort('amount_cny')}>人民币</Table.HeaderCell>
          <Table.HeaderCell sorted={sorted('request_count')} onClick={() => handleSort('request_count')}>请求数</Table.HeaderCell>
        </Table.Row>
      </Table.Header>
      <Table.Body>
        {data.map((row, idx) => (
          <Table.Row key={row.token_name || idx}>
            <Table.Cell>{row.token_name ?? '-'}</Table.Cell>
            <Table.Cell>{row.project_number ?? '-'}</Table.Cell>
            <Table.Cell>{(row.tokens || 0).toLocaleString()}</Table.Cell>
            <Table.Cell>${amountValue(row.amount_usd).toFixed(6)}</Table.Cell>
            <Table.Cell>¥{amountValue(row.amount_cny).toFixed(4)}</Table.Cell>
            <Table.Cell>{(row.request_count || 0).toLocaleString()}</Table.Cell>
          </Table.Row>
        ))}
        {rows.length > 0 && (
          <TotalsRow
            label='合计'
            cells={[
              '-',
              totals.tokens.toLocaleString(),
              `$${totals.amount_usd.toFixed(6)}`,
              `¥${totals.amount_cny.toFixed(4)}`,
              totals.request_count.toLocaleString(),
            ]}
          />
        )}
      </Table.Body>
    </Table>
  );
};

const ChannelTable = ({ rows }) => {
  const [data, setData] = useState(rows);
  const [sortColumn, setSortColumn] = useState(null);
  const [sortDirection, setSortDirection] = useState('ascending');

  useEffect(() => {
    setData(rows);
    setSortColumn(null);
    setSortDirection('ascending');
  }, [rows]);

  const handleSort = (column) => {
    const newDirection =
      sortColumn === column && sortDirection === 'ascending'
        ? 'descending'
        : 'ascending';
    setSortColumn(column);
    setSortDirection(newDirection);
    setData(sortData(rows, column, newDirection));
  };

  const sorted = (col) => (sortColumn === col ? sortDirection : null);

  const totals = rows.reduce(
    (acc, r) => ({
      tokens: acc.tokens + (r.tokens || 0),
      amount_usd: acc.amount_usd + amountValue(r.amount_usd),
      amount_cny: acc.amount_cny + amountValue(r.amount_cny),
      request_count: acc.request_count + (r.request_count || 0),
    }),
    { tokens: 0, amount_usd: 0, amount_cny: 0, request_count: 0 }
  );

  return (
    <Table celled sortable>
      <Table.Header>
        <Table.Row>
          <Table.HeaderCell sorted={sorted('channel_id')} onClick={() => handleSort('channel_id')}>渠道ID</Table.HeaderCell>
          <Table.HeaderCell sorted={sorted('channel_name')} onClick={() => handleSort('channel_name')}>渠道名称</Table.HeaderCell>
          <Table.HeaderCell sorted={sorted('tokens')} onClick={() => handleSort('tokens')}>Token消耗</Table.HeaderCell>
          <Table.HeaderCell sorted={sorted('amount_usd')} onClick={() => handleSort('amount_usd')}>美元</Table.HeaderCell>
          <Table.HeaderCell sorted={sorted('amount_cny')} onClick={() => handleSort('amount_cny')}>人民币</Table.HeaderCell>
          <Table.HeaderCell sorted={sorted('request_count')} onClick={() => handleSort('request_count')}>请求数</Table.HeaderCell>
        </Table.Row>
      </Table.Header>
      <Table.Body>
        {data.map((row, idx) => (
          <Table.Row key={row.channel_id || idx}>
            <Table.Cell>{row.channel_id ?? '-'}</Table.Cell>
            <Table.Cell>{row.channel_name ?? '-'}</Table.Cell>
            <Table.Cell>{(row.tokens || 0).toLocaleString()}</Table.Cell>
            <Table.Cell>${amountValue(row.amount_usd).toFixed(6)}</Table.Cell>
            <Table.Cell>¥{amountValue(row.amount_cny).toFixed(4)}</Table.Cell>
            <Table.Cell>{(row.request_count || 0).toLocaleString()}</Table.Cell>
          </Table.Row>
        ))}
        {rows.length > 0 && (
          <TotalsRow
            label='合计'
            cells={[
              '-',
              '-',
              totals.tokens.toLocaleString(),
              `$${totals.amount_usd.toFixed(6)}`,
              `¥${totals.amount_cny.toFixed(4)}`,
              totals.request_count.toLocaleString(),
            ]}
          />
        )}
      </Table.Body>
    </Table>
  );
};

const ChannelTokenTable = ({ rows }) => {
  const [data, setData] = useState(rows);
  const [sortColumn, setSortColumn] = useState(null);
  const [sortDirection, setSortDirection] = useState('ascending');

  useEffect(() => {
    setData(rows);
    setSortColumn(null);
    setSortDirection('ascending');
  }, [rows]);

  const handleSort = (column) => {
    const newDirection =
      sortColumn === column && sortDirection === 'ascending'
        ? 'descending'
        : 'ascending';
    setSortColumn(column);
    setSortDirection(newDirection);
    setData(sortData(rows, column, newDirection));
  };

  const sorted = (col) => (sortColumn === col ? sortDirection : null);

  const totals = rows.reduce(
    (acc, r) => ({
      tokens: acc.tokens + (r.tokens || 0),
      amount_usd: acc.amount_usd + amountValue(r.amount_usd),
      amount_cny: acc.amount_cny + amountValue(r.amount_cny),
      request_count: acc.request_count + (r.request_count || 0),
    }),
    { tokens: 0, amount_usd: 0, amount_cny: 0, request_count: 0 }
  );

  return (
    <Table celled sortable>
      <Table.Header>
        <Table.Row>
          <Table.HeaderCell sorted={sorted('channel_id')} onClick={() => handleSort('channel_id')}>渠道ID</Table.HeaderCell>
          <Table.HeaderCell sorted={sorted('channel_name')} onClick={() => handleSort('channel_name')}>渠道名称</Table.HeaderCell>
          <Table.HeaderCell sorted={sorted('token_name')} onClick={() => handleSort('token_name')}>令牌名称</Table.HeaderCell>
          <Table.HeaderCell sorted={sorted('project_number')} onClick={() => handleSort('project_number')}>项目编号</Table.HeaderCell>
          <Table.HeaderCell sorted={sorted('tokens')} onClick={() => handleSort('tokens')}>Token消耗</Table.HeaderCell>
          <Table.HeaderCell sorted={sorted('amount_usd')} onClick={() => handleSort('amount_usd')}>美元</Table.HeaderCell>
          <Table.HeaderCell sorted={sorted('amount_cny')} onClick={() => handleSort('amount_cny')}>人民币</Table.HeaderCell>
          <Table.HeaderCell sorted={sorted('request_count')} onClick={() => handleSort('request_count')}>请求数</Table.HeaderCell>
        </Table.Row>
      </Table.Header>
      <Table.Body>
        {data.map((row, idx) => (
          <Table.Row key={`${row.channel_id}_${row.token_name}_${idx}`}>
            <Table.Cell>{row.channel_id ?? '-'}</Table.Cell>
            <Table.Cell>{row.channel_name ?? '-'}</Table.Cell>
            <Table.Cell>{row.token_name ?? '-'}</Table.Cell>
            <Table.Cell>{row.project_number ?? '-'}</Table.Cell>
            <Table.Cell>{(row.tokens || 0).toLocaleString()}</Table.Cell>
            <Table.Cell>${amountValue(row.amount_usd).toFixed(6)}</Table.Cell>
            <Table.Cell>¥{amountValue(row.amount_cny).toFixed(4)}</Table.Cell>
            <Table.Cell>{(row.request_count || 0).toLocaleString()}</Table.Cell>
          </Table.Row>
        ))}
        {rows.length > 0 && (
          <TotalsRow
            label='合计'
            cells={[
              '-',
              '-',
              '-',
              totals.tokens.toLocaleString(),
              `$${totals.amount_usd.toFixed(6)}`,
              `¥${totals.amount_cny.toFixed(4)}`,
              totals.request_count.toLocaleString(),
            ]}
          />
        )}
      </Table.Body>
    </Table>
  );
};

const ChannelModelTokenTable = ({ rows }) => {
  const [data, setData] = useState(rows);
  const [sortColumn, setSortColumn] = useState(null);
  const [sortDirection, setSortDirection] = useState('ascending');

  useEffect(() => {
    setData(rows);
    setSortColumn(null);
    setSortDirection('ascending');
  }, [rows]);

  const handleSort = (column) => {
    const newDirection =
      sortColumn === column && sortDirection === 'ascending'
        ? 'descending'
        : 'ascending';
    setSortColumn(column);
    setSortDirection(newDirection);
    setData(sortData(rows, column, newDirection));
  };

  const sorted = (col) => (sortColumn === col ? sortDirection : null);

  const totals = rows.reduce(
    (acc, r) => ({
      tokens: acc.tokens + (r.tokens || 0),
      prompt_tokens: acc.prompt_tokens + (r.prompt_tokens || 0),
      completion_tokens: acc.completion_tokens + (r.completion_tokens || 0),
      cache_creation_tokens:
        acc.cache_creation_tokens + (r.cache_creation_tokens || 0),
      cache_read_tokens: acc.cache_read_tokens + (r.cache_read_tokens || 0),
      amount_usd: acc.amount_usd + amountValue(r.amount_usd),
      amount_cny: acc.amount_cny + amountValue(r.amount_cny),
      request_count: acc.request_count + (r.request_count || 0),
    }),
    {
      tokens: 0, prompt_tokens: 0, completion_tokens: 0,
      cache_creation_tokens: 0, cache_read_tokens: 0,
      amount_usd: 0, amount_cny: 0, request_count: 0,
    }
  );

  const hasCache =
    totals.cache_creation_tokens > 0 || totals.cache_read_tokens > 0;

  return (
    <Table celled sortable>
      <Table.Header>
        <Table.Row>
          <Table.HeaderCell sorted={sorted('channel_id')} onClick={() => handleSort('channel_id')}>渠道ID</Table.HeaderCell>
          <Table.HeaderCell sorted={sorted('channel_name')} onClick={() => handleSort('channel_name')}>渠道名称</Table.HeaderCell>
          <Table.HeaderCell sorted={sorted('model_name')} onClick={() => handleSort('model_name')}>模型名称</Table.HeaderCell>
          <Table.HeaderCell sorted={sorted('token_id')} onClick={() => handleSort('token_id')}>令牌ID</Table.HeaderCell>
          <Table.HeaderCell sorted={sorted('token_name')} onClick={() => handleSort('token_name')}>令牌名称</Table.HeaderCell>
          <Table.HeaderCell sorted={sorted('project_number')} onClick={() => handleSort('project_number')}>项目编号</Table.HeaderCell>
          <Table.HeaderCell sorted={sorted('tokens')} onClick={() => handleSort('tokens')}>Token消耗</Table.HeaderCell>
          <Table.HeaderCell sorted={sorted('usage_percent')} onClick={() => handleSort('usage_percent')}>占比</Table.HeaderCell>
          <Table.HeaderCell>提示Token</Table.HeaderCell>
          <Table.HeaderCell>补全Token</Table.HeaderCell>
          {hasCache && <Table.HeaderCell>缓存创建Token</Table.HeaderCell>}
          {hasCache && <Table.HeaderCell>缓存读取Token</Table.HeaderCell>}
          <Table.HeaderCell sorted={sorted('amount_usd')} onClick={() => handleSort('amount_usd')}>美元</Table.HeaderCell>
          <Table.HeaderCell sorted={sorted('amount_cny')} onClick={() => handleSort('amount_cny')}>人民币</Table.HeaderCell>
          <Table.HeaderCell sorted={sorted('request_count')} onClick={() => handleSort('request_count')}>请求数</Table.HeaderCell>
        </Table.Row>
      </Table.Header>
      <Table.Body>
        {data.map((row, idx) => (
          <Table.Row key={`${row.channel_id}_${row.model_name}_${row.token_id}_${idx}`}>
            <Table.Cell>{row.channel_id ?? '-'}</Table.Cell>
            <Table.Cell>{row.channel_name ?? '-'}</Table.Cell>
            <Table.Cell>{row.model_name ?? '-'}</Table.Cell>
            <Table.Cell>{row.token_id ?? '-'}</Table.Cell>
            <Table.Cell>{row.token_name ?? '-'}</Table.Cell>
            <Table.Cell>{row.project_number ?? '-'}</Table.Cell>
            <Table.Cell>{(row.tokens || 0).toLocaleString()}</Table.Cell>
            <Table.Cell>{percentValue(row.usage_percent).toFixed(2)}%</Table.Cell>
            <Table.Cell>{(row.prompt_tokens || 0).toLocaleString()}</Table.Cell>
            <Table.Cell>{(row.completion_tokens || 0).toLocaleString()}</Table.Cell>
            {hasCache && <Table.Cell>{(row.cache_creation_tokens || 0).toLocaleString()}</Table.Cell>}
            {hasCache && <Table.Cell>{(row.cache_read_tokens || 0).toLocaleString()}</Table.Cell>}
            <Table.Cell>${amountValue(row.amount_usd).toFixed(6)}</Table.Cell>
            <Table.Cell>¥{amountValue(row.amount_cny).toFixed(4)}</Table.Cell>
            <Table.Cell>{(row.request_count || 0).toLocaleString()}</Table.Cell>
          </Table.Row>
        ))}
        {rows.length > 0 && (
          <TotalsRow
            label='合计'
            cells={[
              '-',
              '-',
              '-',
              '-',
              '-',
              totals.tokens.toLocaleString(),
              '-',
              totals.prompt_tokens.toLocaleString(),
              totals.completion_tokens.toLocaleString(),
              ...(hasCache
                ? [totals.cache_creation_tokens.toLocaleString(), totals.cache_read_tokens.toLocaleString()]
                : []),
              `$${totals.amount_usd.toFixed(6)}`,
              `¥${totals.amount_cny.toFixed(4)}`,
              totals.request_count.toLocaleString(),
            ]}
          />
        )}
      </Table.Body>
    </Table>
  );
};

// ---- Main Component ----

const DashboardStats = () => {
  const { t } = useTranslation();
  const [defaultDateRange] = useState(getDefaultDateRange);

  const [startTime, setStartTime] = useState(defaultDateRange.start);
  const [endTime, setEndTime] = useState(defaultDateRange.end);
  const [period, setPeriod] = useState('d');
  const [projectNumber, setProjectNumber] = useState('');
  const [tokenName, setTokenName] = useState('');
  const [channelId, setChannelId] = useState('');

  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const buildQueryParams = (extra = {}) => {
    const params = new URLSearchParams();
    const st = toBoundaryTimestamp(startTime, false);
    const et = toBoundaryTimestamp(endTime, true);
    if (st) params.append('start_time', st);
    if (et) params.append('end_time', et);
    params.append('period', period);
    if (projectNumber.trim()) {
      projectNumber.split(',').forEach((v) => {
        const trimmed = v.trim();
        if (trimmed) params.append('project_number', trimmed);
      });
    }
    if (tokenName.trim()) {
      tokenName.split(',').forEach((v) => {
        const trimmed = v.trim();
        if (trimmed) params.append('token_name', trimmed);
      });
    }
    if (channelId.trim()) {
      channelId.split(',').forEach((v) => {
        const trimmed = v.trim();
        if (trimmed && /^[1-9]\d*$/.test(trimmed)) params.append('channel_id', trimmed);
      });
    }
    Object.entries(extra).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        params.append(key, value);
      }
    });
    return params;
  };

  const fetchStats = async () => {
    setLoading(true);
    setError(null);
    try {
      const params = buildQueryParams();

      const res = await API.get(`/api/user/dashboard/stats?${params}`);
      if (res.data.success) {
        setStats(res.data.data);
      } else {
        setError(res.data.message || '加载统计数据失败');
      }
    } catch (err) {
      setError('加载统计数据失败');
      showError('加载统计数据失败');
    } finally {
      setLoading(false);
    }
  };

  const handleExportExcel = () => {
    const params = buildQueryParams({ format: 'xlsx' });
    window.location.href = `/api/user/dashboard/export?${params}`;
  };

  useEffect(() => {
    fetchStats();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleReset = () => {
    const nextDefaultRange = getDefaultDateRange();
    setStartTime(nextDefaultRange.start);
    setEndTime(nextDefaultRange.end);
    setPeriod('d');
    setProjectNumber('');
    setTokenName('');
    setChannelId('');
  };

  const handlePeriodChange = (_, { value }) => {
    const normalized = normalizeDateRangeForPeriod(startTime, endTime, value);
    setStartTime(normalized.start);
    setEndTime(normalized.end);
    setPeriod(value);
  };

  const dateInput = periodInputMeta(period);

  const summary = stats?.summary || {};
  const amountUSD = normalizeNumeric(summary.amount_usd) ?? 0;
  const amountCNY = normalizeNumeric(summary.amount_cny) ?? 0;

  const hasTables =
    (stats?.token_model_stats && stats.token_model_stats.length > 0) ||
    (stats?.model_stats && stats.model_stats.length > 0) ||
    (stats?.token_stats && stats.token_stats.length > 0) ||
    (stats?.channel_stats && stats.channel_stats.length > 0) ||
    (stats?.channel_token_stats && stats.channel_token_stats.length > 0) ||
    (stats?.channel_model_token_stats && stats.channel_model_token_stats.length > 0);

  const tablePanes = [
    {
      menuItem: '令牌模型统计',
      render: () => (
        <Tab.Pane>
          {stats?.token_model_stats?.length > 0 ? (
            <TokenModelTable rows={stats.token_model_stats} />
          ) : (
            <p style={{ color: '#999', textAlign: 'center', padding: '40px 0' }}>暂无数据</p>
          )}
        </Tab.Pane>
      ),
    },
    {
      menuItem: '模型统计',
      render: () => (
        <Tab.Pane>
          {stats?.model_stats?.length > 0 ? (
            <ModelTable rows={stats.model_stats} />
          ) : (
            <p style={{ color: '#999', textAlign: 'center', padding: '40px 0' }}>暂无数据</p>
          )}
        </Tab.Pane>
      ),
    },
    {
      menuItem: '令牌统计',
      render: () => (
        <Tab.Pane>
          {stats?.token_stats?.length > 0 ? (
            <TokenTable rows={stats.token_stats} />
          ) : (
            <p style={{ color: '#999', textAlign: 'center', padding: '40px 0' }}>暂无数据</p>
          )}
        </Tab.Pane>
      ),
    },
    {
      menuItem: '渠道统计',
      render: () => (
        <Tab.Pane>
          {stats?.channel_stats?.length > 0 ? (
            <ChannelTable rows={stats.channel_stats} />
          ) : (
            <p style={{ color: '#999', textAlign: 'center', padding: '40px 0' }}>暂无数据</p>
          )}
        </Tab.Pane>
      ),
    },
    {
      menuItem: '令牌渠道统计',
      render: () => (
        <Tab.Pane>
          {stats?.channel_token_stats?.length > 0 ? (
            <ChannelTokenTable rows={stats.channel_token_stats} />
          ) : (
            <p style={{ color: '#999', textAlign: 'center', padding: '40px 0' }}>暂无数据</p>
          )}
        </Tab.Pane>
      ),
    },
    {
      menuItem: '渠道模型令牌统计',
      render: () => (
        <Tab.Pane>
          {stats?.channel_model_token_stats?.length > 0 ? (
            <ChannelModelTokenTable rows={stats.channel_model_token_stats} />
          ) : (
            <p style={{ color: '#999', textAlign: 'center', padding: '40px 0' }}>暂无数据</p>
          )}
        </Tab.Pane>
      ),
    },
  ];

  return (
    <div className='dashboard-stats'>
      <Container fluid style={{ padding: '20px' }}>
        <Header as='h2'>{t('header.stats', { defaultValue: '统计' })}</Header>

        <Segment>
          <Form>
            <Form.Group widths='equal'>
              <Form.Field>
                <label>{dateInput.startLabel}</label>
                <input
                  type={dateInput.type}
                  value={toPeriodInputValue(startTime, period)}
                  onChange={(e) =>
                    setStartTime(fromPeriodInputValue(e.target.value, period, false))
                  }
                />
              </Form.Field>
              <Form.Field>
                <label>{dateInput.endLabel}</label>
                <input
                  type={dateInput.type}
                  value={toPeriodInputValue(endTime, period)}
                  onChange={(e) =>
                    setEndTime(fromPeriodInputValue(e.target.value, period, true))
                  }
                />
              </Form.Field>
              <Form.Field>
                <label>统计周期</label>
                <Select
                  options={PERIOD_OPTIONS}
                  value={period}
                  onChange={handlePeriodChange}
                  fluid
                />
              </Form.Field>
              <Form.Field>
                <label>&nbsp;</label>
                <Button primary onClick={fetchStats} loading={loading}>查询</Button>
                <Button onClick={handleReset}>重置</Button>
                <Button color='green' onClick={handleExportExcel} disabled={loading}>导出Excel</Button>
              </Form.Field>
            </Form.Group>
            <Form.Group widths='equal'>
              <Form.Field>
                <label>项目编号</label>
                <input
                  placeholder='可选，逗号分隔'
                  value={projectNumber}
                  onChange={(e) => setProjectNumber(e.target.value)}
                />
              </Form.Field>
              <Form.Field>
                <label>令牌名称</label>
                <input
                  placeholder='可选'
                  value={tokenName}
                  onChange={(e) => setTokenName(e.target.value)}
                />
              </Form.Field>
              <Form.Field>
                <label>渠道ID</label>
                <input
                  placeholder='可选'
                  value={channelId}
                  onChange={(e) => setChannelId(e.target.value)}
                />
              </Form.Field>
              <Form.Field />
            </Form.Group>
          </Form>
        </Segment>

        {loading ? (
          <Segment style={{ minHeight: '400px' }}>
            <Dimmer active inverted>
              <Loader>加载中...</Loader>
            </Dimmer>
          </Segment>
        ) : error ? (
          <Message negative>
            <Message.Header>加载失败</Message.Header>
            <p>{error}</p>
          </Message>
        ) : stats ? (
          <>
            <Segment>
              <Header as='h3' style={{ marginBottom: '20px' }}>数据概览</Header>
              <Card.Group itemsPerRow={4} stackable>
                <Card>
                  <Card.Content>
                    <Statistic size='small'>
                      <Statistic.Value>
                        {(summary.total_tokens || 0).toLocaleString()}
                      </Statistic.Value>
                      <Statistic.Label>Token总消耗</Statistic.Label>
                    </Statistic>
                    <div style={{ marginTop: '10px', fontSize: '12px', color: '#666' }}>
                      <div>Prompt: {(summary.prompt_tokens || 0).toLocaleString()}</div>
                      <div>Completion: {(summary.completion_tokens || 0).toLocaleString()}</div>
                      {((summary.cache_creation_tokens || 0) > 0 ||
                        (summary.cache_read_tokens || 0) > 0) && (
                        <>
                          <div>
                            Cache Write: {(summary.cache_creation_tokens || 0).toLocaleString()}
                          </div>
                          <div>
                            Cache Read: {(summary.cache_read_tokens || 0).toLocaleString()}
                          </div>
                        </>
                      )}
                    </div>
                  </Card.Content>
                </Card>

                <Card>
                  <Card.Content>
                    <Statistic size='small' color='green'>
                      <Statistic.Value>${amountUSD.toFixed(4)}</Statistic.Value>
                      <Statistic.Label>美元消费</Statistic.Label>
                    </Statistic>
                  </Card.Content>
                </Card>

                <Card>
                  <Card.Content>
                    <Statistic size='small' color='orange'>
                      <Statistic.Value>¥{amountCNY.toFixed(4)}</Statistic.Value>
                      <Statistic.Label>人民币消费</Statistic.Label>
                    </Statistic>
                  </Card.Content>
                </Card>

                <Card>
                  <Card.Content>
                    <Statistic size='small' color='blue'>
                      <Statistic.Value>
                        {(summary.request_count || 0).toLocaleString()}
                      </Statistic.Value>
                      <Statistic.Label>请求总数</Statistic.Label>
                    </Statistic>
                  </Card.Content>
                </Card>
              </Card.Group>
            </Segment>

            <Segment>
              <Header as='h3' style={{ marginBottom: '20px' }}>明细统计</Header>
              {hasTables ? (
                <Tab panes={tablePanes} />
              ) : (
                <p style={{ color: '#999', textAlign: 'center', padding: '40px 0' }}>暂无明细数据</p>
              )}
            </Segment>
          </>
        ) : (
          <Segment>
            <p style={{ color: '#999', textAlign: 'center', padding: '40px 0' }}>暂无数据</p>
          </Segment>
        )}
      </Container>
    </div>
  );
};

export default DashboardStats;
